import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { arrayToObject, take } from 'lib/array/arrayUtils'
import OfferCalendarDaysMonth from './OfferCalendarDaysMonth'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import {
  getMultiRoomCalendarDays,
  getMultiRoomDaysOtherAvailability,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import type { MonthDayView } from 'selectors/offerPage/offerPageSelectors'
import {
  getCalendarDays,
  getDaysOtherAvailability,
  getDurationOptions,
  getFixedFlashLengthOfStayCheckInCalendarDays,
  getFixedFlashLengthOfStayCalendarDays,
  getFixedFlashLengthOfStayCheckoutCalendarDays,
  getFixedFlashLengthOfStayMaximumDuration,
  getSelectedRoomRateId,
} from 'selectors/offerPage/offerPageSelectors'
import { getFirstAvailableDuration } from 'components/OfferPage/FlashHotel/flashHotelCalendarSkipUtils'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import { fadeIn } from 'components/utils/animations'
import OfferCalendarPriceBlurb from '../OfferCalendarPriceBlurb'
import useMountedEffect from 'hooks/useMountedEffect'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import type { TrackableProduct } from 'components/OfferList/OfferListEventsContext'
import { trackCalendarMonthImpression } from 'analytics/calendarMonthImpression'
import { useIntl } from 'react-intl'
import CalendarMonthPriceBlockImpression from '../../CalendarMonthPriceBlockImpression'
import { type CalendarMonthImpressionPrice, getCalendarMonthImpressionPrice } from '../../calendarMonthImpressionPrice'
import { EmptyObject } from 'lib/object/objectUtils'

const Root = styled.div`
  width: 100%;
`

const Calendar = styled(VerticalSpacer)`
  animation: ${fadeIn} 0.2s;
`

interface Props {
  daysByMonth: Array<MonthDayView>
  className?: string
  onBackToMonth?: () => void
  initialMonth?: string
  room?: App.PackageRoomType
  otherAvailability: { [checkIn: string]: boolean }
  selectedCheckIn?: string
  selectedCheckOut?: string
  checkoutCalendarDays: Record<string, App.CalendarDay>
  checkInCalendarDays: Record<string, App.CalendarDay>
  maximumDuration?: number
  defaultDayPicker?: boolean
  flightsOnly?: boolean
  contentRef: React.RefObject<HTMLDivElement | null>
  isAnyFlightsActive?: boolean
  bundleOfferId?: string
  displayMemberPrice: boolean
  isFixedFlashLengthOfStayEnabled?: boolean
  minimumDuration?: number
  offer?: TrackableProduct
  roomRateId?: string
  onRegisterClearHoveredCheckOut?: (clearHoveredCheckOut?: () => void) => void
}

function OfferCalendarDayPicker(props: Props) {
  const {
    daysByMonth,
    className,
    initialMonth,
    otherAvailability,
    room,
    selectedCheckIn,
    selectedCheckOut,
    checkoutCalendarDays,
    checkInCalendarDays,
    maximumDuration,
    flightsOnly,
    contentRef,
    isAnyFlightsActive,
    bundleOfferId,
    displayMemberPrice,
    isFixedFlashLengthOfStayEnabled = false,
    minimumDuration,
    offer,
    roomRateId,
    onRegisterClearHoveredCheckOut,
  } = props
  // always show month selected, previous month and next month
  const initialMonthIndex = useRef(daysByMonth.findIndex((entry) => entry[1][0]?.monthKey === initialMonth)).current
  const [monthsOnDisplay, setMonthsOnDisplay] = useState({ toShow: 3, offset: Math.max(initialMonthIndex - 1, 0) })
  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)
  const trackedMonthKeys = useRef(new Set<string>())
  const handleMonthImpression = useCallback((monthKey: string, monthNumber: number, year: number, impressionPrice?: CalendarMonthImpressionPrice) => {
    const dedupKey = roomRateId ? `${monthKey}:${roomRateId}` : monthKey
    if (trackedMonthKeys.current.has(dedupKey)) return
    trackedMonthKeys.current.add(dedupKey)
    trackCalendarMonthImpression({
      lastInteractedPriceMember: impressionPrice?.memberPrice,
      lastInteractedPriceNonMember: impressionPrice?.nonMemberPrice,
      monthNumber,
      offer,
      roomRateId,
      year,
    })
  }, [offer, roomRateId])

  useLayoutEffect(() => {
    const initialMonthToDisplay = daysByMonth[initialMonthIndex]
    if (initialMonthToDisplay) {
      const monthKey = initialMonthToDisplay[1][0].monthKey
      monthElements.current[monthKey]?.scrollIntoView({ behavior: 'auto' })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMonthIndex])

  // We discard the initial 'previous in view' check because we already show
  // an extra month before this one on mount
  useMountedEffect(() => {
    if (previousInView) {
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  // mounting layout handles transition from month to day calendar
  useLayoutEffect(() => {
    if (initialMonthIndex > 0) {
      // we need to scroll to the 'middle' month
      const monthKey = daysByMonth[monthsOnDisplay.offset]?.[1]?.[0]?.monthKey
      const monthElement = monthKey ? monthElements.current[monthKey] : undefined
      const scrollPosition = monthElement ? monthElement.offsetHeight + 40 : undefined
      if (scrollPosition) {
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  // on offset change handles when scrolling up and new month appears
  useLayoutEffect(() => {
    if (scrollRestoreMonth.current !== null) {
      const monthKey = daysByMonth[monthsOnDisplay.offset]?.[1]?.[0]?.monthKey
      const monthElement = monthKey ? monthElements.current[monthKey] : undefined
      const scroll = monthElement ? monthElement.offsetHeight + 40 : undefined

      if (scroll) {
        contentRef.current?.scrollTo({ behavior: 'auto', top: scroll })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nextInView) {
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  const monthPrices = useMemo(() => {
    return arrayToObject(daysByMonth, ([title]) => title, ([_, days]) => {
      const withTotals = days.filter(day => {
        if (flightsOnly && !day.flightCacheActive) {
          // we don't have a price to show if flight cache isn't active and we're only showing flights
          return false
        }
        return isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable
      })
      const hotelFromPrice = withTotals.length ? Math.min(...withTotals.map(d => d.hotelTotal)) : 0
      const packagedFromPrice = withTotals.length ? Math.min(...withTotals.map(d => d.packagedTotal)) : 0
      const hotelImpressionPrice = getCalendarMonthImpressionPrice(withTotals, false, displayMemberPrice)
      const packagedImpressionPrice = getCalendarMonthImpressionPrice(withTotals, true, displayMemberPrice)

      return {
        hotelFromPrice,
        hotelImpressionPrice,
        packagedFromPrice,
        packagedImpressionPrice,
      } })
  }, [daysByMonth, displayMemberPrice, flightsOnly, isAnyFlightsActive])

  const fixedFlashPriceBlurbDuration = isFixedFlashLengthOfStayEnabled ? minimumDuration : undefined

  const intl = useIntl()
  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')
  const fixedFlashCheckoutCalendarDays = isFixedFlashLengthOfStayEnabled ? checkoutCalendarDays : EmptyObject
  const fixedFlashCheckInCalendarDays = isFixedFlashLengthOfStayEnabled ? checkInCalendarDays : EmptyObject
  const fixedFlashMaximumDuration = isFixedFlashLengthOfStayEnabled ? maximumDuration : undefined
  const [minimumStayTooltipDate, setMinimumStayTooltipDate] = useState<string>()
  const [maximumStayTooltipDate, setMaximumStayTooltipDate] = useState<string>()
  const [hoveredCheckOut, setHoveredCheckOut] = useState<string>()

  useEffect(() => {
    setHoveredCheckOut(undefined)
  }, [selectedCheckIn, selectedCheckOut])

  useEffect(() => {
    if (!onRegisterClearHoveredCheckOut) {
      return
    }

    onRegisterClearHoveredCheckOut(() => setHoveredCheckOut(undefined))

    return () => {
      onRegisterClearHoveredCheckOut(undefined)
    }
  }, [onRegisterClearHoveredCheckOut])

  const onMinimumStayDisabledClick = useCallback((day: App.CalendarDay) => {
    setMinimumStayTooltipDate(day.checkIn)
    setMaximumStayTooltipDate(undefined)
  }, [])
  const onMaximumStayDisabledClick = useCallback((day: App.CalendarDay) => {
    setMinimumStayTooltipDate(undefined)
    setMaximumStayTooltipDate(day.checkIn)
  }, [])
  const onClearStayTooltips = useCallback(() => {
    setMinimumStayTooltipDate(undefined)
    setMaximumStayTooltipDate(undefined)
  }, [])
  const onHoveredCheckOutChange = useCallback((checkOut?: string) => {
    setHoveredCheckOut(checkOut)
  }, [])

  return (
    <Root className={className}>
      {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
      <VerticalSpacer gap={40}>
        {take(daysByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map(([key, days, { year, monthIndex, tacticalDays }]) => {
          const fromPrice = isAnyFlightsActive ? monthPrices[key].packagedFromPrice : monthPrices[key].hotelFromPrice
          const impressionPrice = isAnyFlightsActive ? monthPrices[key].packagedImpressionPrice : monthPrices[key].hotelImpressionPrice
          const headingTitle = intl.formatDate(new Date(year, monthIndex), INTL_LONG_MONTH_LONG_YEAR)
          const hasMonthBundleAdditionAmount = bundleOfferId && days.some(day => day.surcharge > 0)
          return <Calendar
            key={key}
            gap={20}
            ref={(el) => { monthElements.current[days[0].monthKey] = el }}
          >
            <div>
              <Heading variant="heading3">{headingTitle}</Heading>
              {!!fromPrice && !bundleOfferId && <OfferCalendarPriceBlurb
                year={days[0].year}
                flightDealMonthKey={days[0].monthKey}
                fromPrice={fromPrice}
                priceBlurbDuration={fixedFlashPriceBlurbDuration}
                hasLoyaltyRoomUpgrade={days.some(day => !!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package)}
                hasTactical={tacticalDays.length > 0}
              />}
              {isBundleAndSaveV2Enabled() && hasMonthBundleAdditionAmount && <BodyTextBlock variant="small">
                {intl.formatMessage({
                  id: 'lDnBWmthJa8iRWj4i3Z4',
                  defaultMessage: '* Peak period surcharge',
                  description: 'Text indicating peak period surcharge applies in calendar day picker',
                })}
              </BodyTextBlock>}
            </div>
            <CalendarMonthPriceBlockImpression
              scrollRoot={contentRef.current}
              monthKey={days[0].monthKey}
              monthNumber={monthIndex + 1}
              year={year}
              shouldTrack={Number.isFinite(fromPrice) && fromPrice > 0}
              impressionPrice={impressionPrice}
              onImpression={handleMonthImpression}
            >
              <OfferCalendarDaysMonth
                days={days}
                tacticalDays={tacticalDays}
                selectedCheckOut={selectedCheckOut}
                checkoutCalendarDays={fixedFlashCheckoutCalendarDays}
                checkInCalendarDays={fixedFlashCheckInCalendarDays}
                maximumDuration={fixedFlashMaximumDuration}
                minimumStayTooltipDate={minimumStayTooltipDate}
                maximumStayTooltipDate={maximumStayTooltipDate}
                hoveredCheckOut={hoveredCheckOut}
                onMinimumStayDisabledClick={onMinimumStayDisabledClick}
                onMaximumStayDisabledClick={onMaximumStayDisabledClick}
                onClearStayTooltips={onClearStayTooltips}
                onHoveredCheckOutChange={onHoveredCheckOutChange}
                otherAvailability={otherAvailability}
                isRoomSelected={!!room}
                flightsOnly={flightsOnly}
                tier={tier}
                isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
                minimumDuration={minimumDuration}
                canLoyaltyUpgrade={canLoyaltyUpgrade}
              />
            </CalendarMonthPriceBlockImpression>
          </Calendar>
        })}
      </VerticalSpacer>
      {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < daysByMonth.length && <div ref={nextRef} />}
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState, props: Props) => {
  const state = takeCurrentOfferState(currentState)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  const shouldUseFixedFlashLengthOfStaySelectors = !!props.isFixedFlashLengthOfStayEnabled && !isMultiRoom
  let daysByMonth: Array<MonthDayView>
  if (isMultiRoom) {
    daysByMonth = getMultiRoomCalendarDays(state, appState)
  } else if (shouldUseFixedFlashLengthOfStaySelectors) {
    daysByMonth = getFixedFlashLengthOfStayCalendarDays(appState, state)
  } else {
    daysByMonth = getCalendarDays(appState, state)
  }

  return {
    room: state.room,
    bundleOfferId: state.bundleOffer?.id,
    selectedCheckIn: state.checkInDate,
    selectedCheckOut: state.checkOutDate,
    minimumDuration: shouldUseFixedFlashLengthOfStaySelectors ?
        getFirstAvailableDuration(getDurationOptions(state, appState)) :
      undefined,
    checkoutCalendarDays: shouldUseFixedFlashLengthOfStaySelectors ? getFixedFlashLengthOfStayCheckoutCalendarDays(state, appState) : EmptyObject,
    checkInCalendarDays: shouldUseFixedFlashLengthOfStaySelectors ? getFixedFlashLengthOfStayCheckInCalendarDays(state, appState) : EmptyObject,
    maximumDuration: shouldUseFixedFlashLengthOfStaySelectors ? getFixedFlashLengthOfStayMaximumDuration(state, appState) : undefined,
    daysByMonth,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    isAnyFlightsActive: state.flights,
    displayMemberPrice: currentState.displayMemberPrice,
    open: !!state.activeFilter,
    offer: state.offer as TrackableProduct | undefined,
    roomRateId: getSelectedRoomRateId(state),
  }
}, OfferCalendarDayPicker, preventRenderWithClosedFilter)
